import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Cpu, Layers, Wrench, Settings, ChevronRight,
  Zap, Database, GitBranch, Box, BarChart2, Menu, X
} from 'lucide-react';

const NAV = [
  { path: '/', label: 'Dashboard', icon: BarChart2 },
  { path: '/jobs', label: 'Jobs', icon: Layers },
  { path: '/new-job', label: 'New Job', icon: Zap },
  { path: '/machines', label: 'Machines', icon: Cpu },
  { path: '/parts', label: 'Part Library', icon: Box },
  { path: '/rules', label: 'Eng. Rules', icon: GitBranch },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--forge-bg)' }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, flexShrink: 0,
        background: 'var(--forge-surface)',
        borderRight: '1px solid var(--forge-border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 100,
        transform: mobileOpen ? 'translateX(0)' : undefined,
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px 18px 16px',
          borderBottom: '1px solid var(--forge-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32,
              background: 'var(--forge-accent)',
              borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Wrench size={16} color="#0a0b0d" strokeWidth={2.5} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, letterSpacing: '-0.03em', color: 'var(--forge-text)' }}>ForgeIQ</div>
              <div style={{ fontSize: 9, color: 'var(--forge-text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: -1 }}>Cold Forging AI</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {NAV.map(({ path, label, icon: Icon }) => {
            const active = loc.pathname === path || (path !== '/' && loc.pathname.startsWith(path));
            return (
              <Link key={path} to={path} style={{ textDecoration: 'none' }}
                onClick={() => setMobileOpen(false)}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '8px 10px', borderRadius: 6, marginBottom: 1,
                  background: active ? 'var(--forge-accent-dim)' : 'transparent',
                  color: active ? 'var(--forge-accent)' : 'var(--forge-text2)',
                  transition: 'all 0.12s',
                  fontSize: 12, fontWeight: 500,
                  cursor: 'pointer',
                  border: active ? '1px solid rgba(232,124,46,0.2)' : '1px solid transparent',
                }}>
                  <Icon size={14} strokeWidth={active ? 2.5 : 2} />
                  {label}
                  {active && <ChevronRight size={10} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Version badge */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--forge-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Database size={10} color="var(--forge-text3)" />
            <span style={{ fontSize: 10, color: 'var(--forge-text3)', letterSpacing: '0.05em' }}>
              Eng. Rules v2.1 · 6 machines
            </span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--forge-text3)', marginTop: 2 }}>
            8 templates · 11 materials
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, marginLeft: 220, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, padding: '28px 32px', maxWidth: 1200 }}>
          {children}
        </div>
      </main>
    </div>
  );
}
